import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  analyzeDseScoreCompleteness,
  DSE_COMPULSORY_SUBJECT_CODES,
} from '../../utils/dseScoreCompleteness';
import { getSubjectFullName, getSubjectShortLabel } from '../../utils/dseSubjects';
import { EDVISE_THEME_BLUE } from '../../utils/jupasOrderDisplay';
import JupasSplitPill from './JupasSplitPill';

const COMPULSORY_COLOR = EDVISE_THEME_BLUE;
const ELECTIVE_COLOR = EDVISE_THEME_BLUE;
const DISPLAY_ORDER = ['CHI', 'ENG', 'MAT', 'CSD'];

function formatGrade(code, score, attainedLabel) {
  if (code === 'CSD') {
    const raw = String(score || '');
    if (!raw) return '—';
    if (/attained|^a$|達到|达到|達標|达标/i.test(raw) || raw === attainedLabel) return attainedLabel;
    return raw;
  }
  return score || '—';
}

/**
 * Compact informative score rail for collapsed Step 1.
 */
export default function JupasScoreSidebar({ scores = [], className = '' }) {
  const { t } = useTranslation();
  const attainedLabel = t('dse.attained');
  const analysis = useMemo(() => analyzeDseScoreCompleteness(scores), [scores]);

  const pills = useMemo(() => {
    const items = [];
    DISPLAY_ORDER.forEach((code) => {
      const row = analysis.byCode.get(code);
      const shortLabel = getSubjectShortLabel(t, code);
      if (row) {
        items.push({
          key: code,
          left: shortLabel,
          right: formatGrade(code, row.score, attainedLabel),
          color: COMPULSORY_COLOR,
          variant: 'score',
          title: getSubjectFullName(t, code),
        });
      } else {
        items.push({
          key: `missing-${code}`,
          left: shortLabel,
          right: '—',
          variant: 'warning',
          title: t('jupa.workspace.missingCompulsoryScore', { code: shortLabel }),
        });
      }
    });

    analysis.completed.forEach((row) => {
      if (DSE_COMPULSORY_SUBJECT_CODES.includes(row.subjectCode)) return;
      items.push({
        key: row.subjectCode,
        left: getSubjectShortLabel(t, row.subjectCode),
        right: row.score,
        color: ELECTIVE_COLOR,
        variant: 'score',
        title: getSubjectFullName(t, row.subjectCode),
      });
    });

    if (!analysis.hasElective) {
      items.push({
        key: 'need-elective',
        left: t('jupa.workspace.electiveShort'),
        right: t('jupa.workspace.addElectiveShort'),
        variant: 'warning',
        title: t('jupa.workspace.addAtLeastOneElective'),
      });
    }

    return items;
  }, [analysis, attainedLabel, t]);

  return (
    <div className={`flex min-h-0 flex-col gap-1.5 overflow-y-auto ${className}`}>
      {pills.map((pill) => (
        <JupasSplitPill
          key={pill.key}
          left={pill.left}
          right={pill.right}
          leftColor={pill.color}
          variant={pill.variant}
          title={pill.title}
        />
      ))}
    </div>
  );
}
